import type { ApiError, ApiResponse } from '@shared/types/api'
import type { CandleResponse, Timeframe } from '@shared/types/candle'
import type { Symbol as TradingSymbol, SymbolSearchResult } from '@shared/types/symbol'
import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

export const http = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
})

http.interceptors.response.use(
  (res) => res,
  (err: unknown) => {
    if (axios.isAxiosError(err) && err.response?.data) {
      const data = err.response.data as ApiError
      return Promise.reject(new Error(data.error?.message ?? 'Request failed'))
    }
    return Promise.reject(err)
  }
)

// ── API functions ─────────────────────────────────────────────────────────────

export async function searchSymbols(
  query: string,
  assetClass?: string
): Promise<SymbolSearchResult[]> {
  const params: Record<string, string> = { q: query }
  if (assetClass) params['class'] = assetClass
  const res = await http.get<ApiResponse<SymbolSearchResult[]>>('/search', { params })
  return res.data.data
}

export type SearchGroups = Record<string, SymbolSearchResult[]>

export interface SearchGroupedResponse {
  data: SearchGroups
  labels: Record<string, string>
  meta: { total: number; groups: number }
}

export async function searchSymbolsGrouped(
  query: string,
  limitPerClass = 5
): Promise<SearchGroupedResponse> {
  const res = await http.get<SearchGroupedResponse>('/search/grouped', {
    params: { q: query, limit_per_class: limitPerClass },
  })
  return res.data
}

export async function getSymbol(ticker: string): Promise<TradingSymbol> {
  const res = await http.get<ApiResponse<TradingSymbol>>(`/symbols/${encodeURIComponent(ticker)}`)
  return res.data.data
}

export async function getCandles(
  ticker: string,
  tf: Timeframe,
  from?: number,
  to?: number,
  limit = 500
): Promise<CandleResponse> {
  const params: Record<string, string | number> = { tf, limit }
  if (from) params['from'] = from
  if (to) params['to'] = to
  const res = await http.get<CandleResponse>(`/candles/${encodeURIComponent(ticker)}`, { params })
  return res.data
}

export async function getNseIndices(): Promise<TradingSymbol[]> {
  const res = await http.get<ApiResponse<TradingSymbol[]>>('/nse/indices')
  return res.data.data
}
